/** Recent views each side keeps, by snapshot number: a delta's baseline must be one of these. */
export const SNAP_RING = 32;
const isObj = (x) => typeof x === 'object' && x !== null;
/** Small leaf objects (a position) are cheaper sent whole than patched. */
function patchable(a, b) {
    if (Array.isArray(a) || Array.isArray(b))
        return Array.isArray(a) && Array.isArray(b) && a.length === b.length;
    return Object.keys(b).length > 3;
}
function same(a, b) {
    if (a === b)
        return true;
    if (!isObj(a) || !isObj(b) || Array.isArray(a) !== Array.isArray(b))
        return false;
    const ka = Object.keys(a).filter((k) => a[k] !== undefined);
    const kb = Object.keys(b).filter((k) => b[k] !== undefined);
    if (ka.length !== kb.length)
        return false;
    for (const k of kb)
        if (!Object.hasOwn(a, k) || !same(a[k], b[k]))
            return false;
    return true;
}
function diff(a, b) {
    const s = {};
    const p = {};
    const d = [];
    let sets = 0;
    let patches = 0;
    for (const k of Object.keys(b)) {
        const nb = b[k];
        if (nb === undefined)
            continue;
        const na = Object.hasOwn(a, k) ? a[k] : undefined;
        if (na !== undefined && same(na, nb))
            continue;
        if (isObj(na) && isObj(nb) && patchable(na, nb)) {
            const sub = diff(na, nb);
            if (sub) {
                p[k] = sub;
                patches++;
            }
        }
        else {
            s[k] = nb;
            sets++;
        }
    }
    if (!Array.isArray(b)) {
        for (const k of Object.keys(a)) {
            if (a[k] !== undefined && (!Object.hasOwn(b, k) || b[k] === undefined))
                d.push(k);
        }
    }
    if (!sets && !patches && !d.length)
        return null;
    const out = {};
    if (sets)
        out.s = s;
    if (patches)
        out.p = p;
    if (d.length)
        out.d = d;
    return out;
}
/** What changed from `base` to `next` (both views for the same player), or null when nothing did. */
export function diffView(base, next) {
    return diff(base, next);
}
function patch(base, p) {
    if (!isObj(base))
        throw new Error('patch on a missing field');
    // copy on write: the baseline stays as it was, unchanged branches are shared
    const out = Array.isArray(base) ? base.slice() : { ...base };
    if (p.d)
        for (const k of p.d)
            delete out[k];
    if (p.s)
        for (const k of Object.keys(p.s))
            out[k] = p.s[k];
    if (p.p)
        for (const k of Object.keys(p.p))
            out[k] = patch(out[k], p.p[k]);
    return out;
}
/** The view a delta describes. Never changes `base`; the result shares its unchanged branches. */
export function patchView(base, p) {
    return p ? patch(base, p) : base;
}
/**
 * The client side: turns `snap` and `snapd` frames back into whole views and keeps the recent ones
 * a later delta may be based on. A delta whose baseline is gone gives null: the client then acks 0
 * and the worker answers with a full snapshot.
 */
export class SnapDecoder {
    ring = [];
    /** Forget every baseline (a new socket: the worker starts again with a full view). */
    reset() {
        this.ring = [];
    }
    /**
     * Read a `snap` or `snapd` frame. Gives the whole view (null when a delta's baseline is gone) and
     * what to ack: the snapshot number, 0 to ask for a whole view, or null when there is nothing to ack.
     */
    take(f) {
        if (f.t === 'snap')
            return { view: this.full(f.sn, f.view), ack: f.sn ?? null };
        const view = this.delta(f.sn, f.base, f.d);
        return { view, ack: view ? f.sn : 0 };
    }
    full(sn, view) {
        if (sn !== undefined)
            this.keep(sn, view);
        return view;
    }
    delta(sn, base, p) {
        const b = this.ring.find((x) => x.sn === base);
        if (!b)
            return null;
        let view;
        try {
            view = patchView(b.view, p);
        }
        catch {
            return null;
        }
        this.keep(sn, view);
        return view;
    }
    keep(sn, view) {
        this.ring.push({ sn, view });
        if (this.ring.length > SNAP_RING * 2)
            this.ring.shift();
    }
}
//# sourceMappingURL=delta.js.map